import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import SphereSearch from '../SphereSearch/SphereSearch';
import DataGridAtom, { DataGridColumn, DataGridAction } from '../../atoms/DataGrid/DataGridAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { Sphere } from 'types/global';
import { FilterExpression } from 'types/filters';
import { RootState } from '@center-pwa/store/store';
import {
  createRelationship,
  deleteRelationship,
  type DeleteRelationshipParams
} from '@center-platform/spheres/sphere';
import { useSchemaType } from '../../../hooks/useSchemaType';
import './RelationshipGridMolecule.scss';

// Hook for detecting mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

export interface Relationship {
  id: string;
  relationshipType: string;
  sourceId: string;
  targetId: string;
  active: boolean;
  createdAt: string;
  createdBy: string;
  deactivatedAt?: string;
  deactivatedBy?: string;
  metadata?: Record<string, any>;
  targetSphere?: Sphere; // Populated after fetching relationship data
}

export interface RelationshipTypeOption {
  label: string;
  value: string;
  description: string;
  target_filter: Record<string, any>;
  target_validation?: FilterExpression[];
}

export interface RelationshipGridProps {
  sphereId: string;
  sphere?: Sphere;
  editable?: boolean;
  organizationId?: string;
  onRelationshipChange?: () => void;
  collapsible?: boolean;
  relationships?: Relationship[];
  relationshipsLoading?: boolean;
  relationshipsError?: string | null;
  onRelationshipsChange?: () => void;
  contextFilters?: FilterExpression[] | null;
  /** Callback to open the dedicated PeriodSelector for applies_to_period relationships */
  onShowPeriodSelector?: () => void;
}

interface RelationshipDialogData {
  relationshipType: string;
  targetSphereId: string;
}

// Mobile Card Component for displaying individual relationships
interface MobileRelationshipCardProps {
  relationship: Relationship;
  relationshipTypeLabel: string;
  editable: boolean;
  sphereId: string;
  onRemove: (relationship: Relationship) => void;
}

const MobileRelationshipCard: React.FC<MobileRelationshipCardProps> = ({
  relationship,
  relationshipTypeLabel,
  editable,
  sphereId,
  onRemove,
}) => {
  const direction = relationship.sourceId === sphereId ? 'Outbound' : 'Inbound';
  const directionIcon = direction === 'Outbound' ? '↗️' : '↙️';

  // Get parent and center info
  const parentPerspective = relationship.targetSphere?.assignedPerspectives?.find(
    p => p.perspectiveProperties?.system_name === 'center_parent'
  );
  const parentSphere = parentPerspective?.assigned?.[0]?.sphere;

  const centerPerspective = relationship.targetSphere?.assignedPerspectives?.find(
    p => p.perspectiveProperties?.system_name === 'center_center'
  );
  const centerSphere = centerPerspective?.assigned?.[0]?.sphere;

  return (
    <div className="mobile-relationship-card">
      <div className="mobile-card-header">
        <div className="relationship-type-badge">
          <i className="pi pi-link" />
          <span>{relationshipTypeLabel}</span>
        </div>
        {!relationship.active && (
          <span className="inactive-badge-mobile">Inactive</span>
        )}
      </div>

      <div className="mobile-card-body">
        <div className="sphere-info">
          <SphereIdentityAtom
            glyph={relationship.targetSphere?.glyph || ''}
            glyphColor={relationship.targetSphere?.glyphColor}
            imageId={relationship.targetSphere?.properties?.imageId || ''}
            sphereName={relationship.targetSphere?.properties?.name || `Sphere ${relationship.targetId}`}
            sphereId={relationship.targetSphere?.id || relationship.targetId}
            size="medium"
          />
        </div>

        {(parentSphere || centerSphere) && (
          <div className="hierarchy-info">
            <i className="pi pi-sitemap" />
            <span className="hierarchy-text">
              {parentSphere?.properties?.name || 'No parent'} • {centerSphere?.properties?.name || 'No center'}
            </span>
          </div>
        )}
      </div>

      <div className="mobile-card-footer">
        <div className="direction-badge">
          <span className="direction-icon">{directionIcon}</span>
          <span className="direction-text">{direction}</span>
        </div>

        {editable && relationship.active && (
          <button
            className="remove-button-mobile"
            onClick={() => onRemove(relationship)}
            aria-label="Remove relationship"
          >
            <i className="pi pi-times" />
            <span>Remove</span>
          </button>
        )}
      </div>
    </div>
  );
};

const RelationshipGridMolecule: React.FC<RelationshipGridProps> = ({
  sphereId,
  sphere,
  editable = true,
  organizationId,
  onRelationshipChange,
  collapsible = true,
  relationships = [],
  relationshipsLoading = false,
  relationshipsError = null,
  onRelationshipsChange,
  contextFilters,
  onShowPeriodSelector,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const toastRef = React.useRef<Toast>(null);
  const isMobile = useIsMobile();

  // Get organizationId from Redux store if not provided as prop
  const storeOrganizationId = useSelector((state: RootState) => state.nodes.organizationId);
  const effectiveOrganizationId = organizationId || storeOrganizationId || '';

  // No longer managing relationships internally - using props

  console.log('🏗️ RelationshipGrid - Component rendered with props:', {
    sphereId,
    organizationId,
    editable,
    collapsible,
    hasSphere: !!sphere,
    relationshipsLength: relationships.length,
    relationshipsLoading,
    relationshipsError
  });
  
  console.log('🔍 RelationshipGrid - OrganizationId resolution:', {
    propOrganizationId: organizationId,
    storeOrganizationId: storeOrganizationId,
    effectiveOrganizationId: effectiveOrganizationId
  });
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [newRelationship, setNewRelationship] = useState<RelationshipDialogData>({
    relationshipType: '',
    targetSphereId: '',
  });
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isAddingRelationship, setIsAddingRelationship] = useState<boolean>(false);
  const [newRelationshipData, setNewRelationshipData] = useState({
    relationshipType: '',
    targetSphere: null as Sphere | null,
  });
  const addRowRef = useRef<HTMLDivElement>(null);

  // All hooks must be called before any conditional returns
  const { schema } = useSchemaType(sphere?.properties?.type ?? null);

  // Scroll to add row when it becomes visible
  useEffect(() => {
    if (isAddingRelationship && addRowRef.current) {
      addRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [isAddingRelationship]);

  // No longer managing relationships internally - using props passed from SphereEditor

  // Get available relationship types from cached schema
  const availableRelationshipTypes = useMemo((): RelationshipTypeOption[] => {
    if (!sphere?.properties?.type) {
      return [];
    }

    if (schema?.relationships) {
      // Canonical Schema always exposes relationships as SchemaRelationship[].
      return schema.relationships.map((relationship): RelationshipTypeOption => {
        const item: RelationshipTypeOption = {
          label: relationship.name || relationship.system_name,
          value: relationship.system_name,
          description: relationship.description || '',
          target_filter: relationship.target_filter || {},
          target_validation: relationship.rules?.target_validation || [],
        };
        return item;
      });
    }

    // Return empty array if no relationships are defined in schema
    return [];
  }, [sphere?.properties?.type, schema]);

  // Helper function to convert target validation rules to search filters
  const convertValidationRulesToFilters = useCallback((validationRules: RelationshipTypeOption['target_validation']) => {
    if (!validationRules || validationRules.length === 0) {
      return [];
    }

    return validationRules.map(rule => {
      switch (rule.operator) {
        case 'is_true':
          return {
            property: rule.property,
            value: true
          };
        case 'is_false':
          return {
            property: rule.property,
            value: false
          };
        case 'equals':
        case 'not_equals':
        case 'greater_than':
        case 'less_than':
        case 'contains':
          // For other operators, we'd need to know the expected value
          // For now, log that we need to handle these cases
          console.warn('Unsupported validation operator for search filter:', rule.operator, rule);
          return null;
        default:
          console.warn('Unknown validation operator:', rule.operator, rule);
          return null;
      }
    }).filter(Boolean); // Remove null entries
  }, []);

  // Helper function to combine target_filter and target_validation into search filters
  const getSearchFiltersForRelationshipType = useCallback((relationshipType: string) => {
    const relationshipTypeOption = availableRelationshipTypes.find((rt: RelationshipTypeOption) => rt.value === relationshipType);
    if (!relationshipTypeOption) {
      console.log('🔍 No relationship type option found for:', relationshipType);
      return {};
    }

    const baseFilters = relationshipTypeOption.target_filter || {};
    const validationFilters = convertValidationRulesToFilters(relationshipTypeOption.target_validation);

    // Convert validation filters to the same format as target_filter
    const additionalFilters: Record<string, any> = {};
    validationFilters.forEach(filter => {
      if (filter) {
        additionalFilters[filter.property] = filter.value;
      }
    });

    // Combine base filters with validation filters
    const combinedFilters = { ...baseFilters, ...additionalFilters };
    
    console.log('🔍 Search filters for relationship type:', {
      relationshipType,
      baseFilters,
      validationRules: relationshipTypeOption.target_validation,
      validationFilters,
      additionalFilters,
      combinedFilters
    });

    return combinedFilters;
  }, [availableRelationshipTypes, convertValidationRulesToFilters]);

  // No longer fetching relationships internally - using props

  // Handle relationship creation using helper
  const handleCreateRelationship = useCallback(async () => {
    if (!apiClients || !newRelationship.relationshipType || !newRelationship.targetSphereId) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both relationship type and target sphere',
        life: 3000,
      });
      return;
    }

    const result = await createRelationship(apiClients.graphClient, {
      fromSphereId: sphereId,
      toSphereId: newRelationship.targetSphereId,
      relationshipType: newRelationship.relationshipType
    });

    if (result.success) {
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Relationship created successfully',
        life: 3000,
      });

      setAddDialogVisible(false);
      setNewRelationship({ relationshipType: '', targetSphereId: '' });
      onRelationshipsChange?.();
      onRelationshipChange?.();
    } else {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: result.error || 'Failed to create relationship',
        life: 5000,
      });
    }
  }, [sphereId, newRelationship, apiClients, effectiveOrganizationId, onRelationshipsChange, onRelationshipChange]);

  // Handle relationship removal using helper
  const handleRemoveRelationship = useCallback(async (relationship: Relationship) => {
    const confirmAction = () => {
      return new Promise<boolean>((resolve) => {
        confirmDialogAtom({
          message: `Are you sure you want to remove the ${relationship.relationshipType} relationship with ${relationship.targetSphere?.properties?.name || relationship.targetId}?`,
          header: 'Confirm Remove',
          icon: 'pi pi-exclamation-triangle',
          variant: 'danger',
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      });
    };

    const confirmed = await confirmAction();
    if (!confirmed || !apiClients) return;

    if (!relationship.id) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Relationship ID is missing',
        life: 5000,
      });
      return;
    }

    const deleteParams: DeleteRelationshipParams = {
      relationshipId: relationship.id,
    };
    
    try {
      const result = await deleteRelationship(apiClients.graphClient, deleteParams);

      if (result.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Relationship removed successfully',
          life: 3000,
        });

        onRelationshipsChange?.();
        onRelationshipChange?.();
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to remove relationship',
          life: 5000,
        });
      }
    } catch (error: any) {
      console.error('Failed to delete relationship:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred while removing the relationship',
        life: 5000,
      });
    }
  }, [apiClients, effectiveOrganizationId, onRelationshipsChange, onRelationshipChange]);

  // Handle relationship type change in inline add
  const handleRelationshipTypeChange = useCallback((relationshipType: string) => {
    // For period relationships, delegate to the dedicated PeriodSelector immediately
    if (relationshipType === 'applies_to_period' && onShowPeriodSelector) {
      setIsAddingRelationship(false);
      onShowPeriodSelector();
      return;
    }
    setNewRelationshipData(prev => ({
      ...prev,
      relationshipType,
      targetSphere: null,
    }));
  }, [onShowPeriodSelector]);

  // Handle creating the relationship from inline add using helper
  const handleInlineCreateRelationship = useCallback(async (e?: React.MouseEvent) => {
    // Prevent any form submission or event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('🔗 Creating relationship with data:', {
      relationshipType: newRelationshipData.relationshipType,
      targetSphere: newRelationshipData.targetSphere?.id,
      availableTypes: availableRelationshipTypes,
      sphereId
    });

    if (!apiClients || !newRelationshipData.relationshipType || !newRelationshipData.targetSphere) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please select both relationship type and target sphere',
        life: 3000,
      });
      return;
    }

    try {
      const result = await createRelationship(apiClients.graphClient, {
        fromSphereId: sphereId,
        toSphereId: newRelationshipData.targetSphere?.id || '',
        relationshipType: newRelationshipData.relationshipType
      });

      if (result.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Relationship created successfully',
          life: 3000,
        });

        // Reset add relationship state
        setIsAddingRelationship(false);
        setNewRelationshipData({
          relationshipType: '',
          targetSphere: null,
        });
        onRelationshipsChange?.();
        onRelationshipChange?.();
      } else {
        console.error('Failed to create relationship:', result.error);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to create relationship',
          life: 5000,
        });
      }
    } catch (error: any) {
      console.error('Unexpected error creating relationship:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error', 
        detail: 'An unexpected error occurred while creating the relationship',
        life: 5000,
      });
    }
  }, [sphereId, newRelationshipData, apiClients, onRelationshipsChange, onRelationshipChange]);

  // Handle canceling inline add relationship
  const handleCancelAddRelationship = useCallback((e?: React.MouseEvent) => {
    // Prevent any form submission or event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsAddingRelationship(false);
    setNewRelationshipData({
      relationshipType: '',
      targetSphere: null,
    });
  }, []);

  // Column definitions
  const columns = useMemo<DataGridColumn[]>(() => [
    {
      field: 'direction',
      header: t('relationshipGrid.direction', 'Direction'),
      sortable: true,
      width: '120px',
      body: (rowData: Relationship) => (
        <span className="relationship-direction">
          {rowData.sourceId === sphereId ? 'Outbound' : 'Inbound'}
        </span>
      ),
    },
    {
      field: 'relationshipType',
      header: t('relationshipGrid.relationshipType', 'Relationship Type'),
      sortable: true,
      width: '200px',
      body: (rowData: Relationship) => (
        <div className="relationship-type">
          <span className="relationship-type-label">
            {availableRelationshipTypes.find((rt: RelationshipTypeOption) => rt.value === rowData.relationshipType)?.label || rowData.relationshipType}
          </span>
          {!rowData.active && (
            <span className="inactive-badge">Inactive</span>
          )}
        </div>
      ),
    },
    {
      field: 'targetSphere.properties.name',
      header: t('relationshipGrid.sphere', 'Sphere'),
      sortable: true,
      width: '250px',
      body: (rowData: Relationship) => (
        <SphereIdentityAtom
          glyph={rowData.targetSphere?.glyph || ''}
          glyphColor={rowData.targetSphere?.glyphColor}
          imageId={rowData.targetSphere?.properties?.imageId || ''}
          sphereName={rowData.targetSphere?.properties?.name || `Sphere ${rowData.targetId}`}
          sphereId={rowData.targetSphere?.id || rowData.targetId}
          size="small"
        />
      ),
    },
    {
      field: 'center',
      header: t('relationshipGrid.center', 'Center'),
      sortable: false,
      width: '200px',
      body: (rowData: Relationship) => {
        if (!effectiveOrganizationId) {
          return <span>Organization ID required</span>;
        }

        // Always show read-only display for center perspective
        const centerPerspective = rowData.targetSphere?.assignedPerspectives?.find(
          p => p.perspectiveProperties?.system_name === 'center_center'
        );
        const centerSphere = centerPerspective?.assigned?.[0]?.sphere;

        return centerSphere ? (
          <SphereIdentityAtom
            glyph={centerSphere.glyph}
            glyphColor={centerSphere.glyphColor}
            imageId={centerSphere.properties?.imageId}
            sphereName={centerSphere.properties?.name}
            sphereId={centerSphere.id}
            size="small"
          />
        ) : (
          <span>No center assigned</span>
        );
      },
    },
    {
      field: 'parent',
      header: t('relationshipGrid.parent', 'Parent'),
      sortable: false,
      width: '200px',
      body: (rowData: Relationship) => {
        if (!effectiveOrganizationId) {
          return <span>Organization ID required</span>;
        }

        // Always show read-only display for parent perspective
        const parentPerspective = rowData.targetSphere?.assignedPerspectives?.find(
          p => p.perspectiveProperties?.system_name === 'center_parent'
        );
        const parentSphere = parentPerspective?.assigned?.[0]?.sphere;

        return parentSphere ? (
          <SphereIdentityAtom
            glyph={parentSphere.glyph}
            glyphColor={parentSphere.glyphColor}
            imageId={parentSphere.properties?.imageId}
            sphereName={parentSphere.properties?.name}
            sphereId={parentSphere.id}
            size="small"
          />
        ) : (
          <span>No parent assigned</span>
        );
      },
    },
  ], [t, availableRelationshipTypes, effectiveOrganizationId, editable, sphereId]);

  // Action definitions
  const actions = useMemo<DataGridAction[]>(() => {
    if (!editable) return [];

    return [
      {
        id: 'remove',
        label: 'Remove',
        icon: 'pi pi-times',
        severity: 'secondary',
        onClick: handleRemoveRelationship,
        visible: (rowData: Relationship) => rowData.active,
      },
    ];
  }, [editable, handleRemoveRelationship]);

  // Header actions
  const headerActions = editable ? (
    <div className="relationship-grid-header-actions">
      <ButtonAtom
        label={isMobile ? undefined : "Add Relationship"}
        icon="pi pi-plus"
        onClick={() => setIsAddingRelationship(true)}
        size="small"
        variant="primary"
        disabled={!!relationshipsError || isAddingRelationship}
        aria-label="Add Relationship"
      />
      {relationshipsError && (
        <ButtonAtom
          label={isMobile ? undefined : "Retry"}
          icon="pi pi-refresh"
          onClick={() => onRelationshipsChange?.()}
          size="small"
          variant="secondary"
          outlined
          aria-label="Retry"
        />
      )}
    </div>
  ) : null;

  // Add relationship dialog
  const addRelationshipDialog = (
    <Dialog
      visible={addDialogVisible}
      style={{ width: '450px' }}
      header="Add New Relationship"
      modal
      className="p-fluid"
      onHide={() => setAddDialogVisible(false)}
    >
      <div className="field">
        <label htmlFor="relationship-type">Relationship Type</label>
        <Dropdown
          id="relationship-type"
          value={newRelationship.relationshipType}
          options={availableRelationshipTypes}
          onChange={(e) => {
            const val = e.value;
            if (val === 'applies_to_period' && onShowPeriodSelector) {
              setAddDialogVisible(false);
              onShowPeriodSelector();
              return;
            }
            setNewRelationship(prev => ({ ...prev, relationshipType: val }));
          }}
          optionLabel="label"
          optionValue="value"
          placeholder="Select relationship type"
          className="w-full"
        />
      </div>

      {newRelationship.relationshipType && (
          <div className="field">
            <label htmlFor="target-sphere">Target Sphere</label>
            <SphereSearch
              relationshipType={newRelationship.relationshipType}
              organizationId={effectiveOrganizationId}
              onSphereSelected={(selectedSpheres, selectedSphere) => {
                setNewRelationship(prev => ({
                  ...prev,
                  targetSphereId: selectedSphere?.code || ''
                }));
              }}
              filters={getSearchFiltersForRelationshipType(newRelationship.relationshipType)}
              filterOperator="and"
              placeholder="Search for sphere to relate..."
              single={true}
              showLabel={false}
              className="w-full dialog-sphere-search"
              contextFilters={contextFilters}
            />
          </div>
      )}

      <div className="dialog-actions">
        <ButtonAtom
          label="Cancel"
          icon="pi pi-times"
          text
          onClick={() => setAddDialogVisible(false)}
          variant="secondary"
        />
        <ButtonAtom
          label="Create"
          icon="pi pi-check"
          onClick={handleCreateRelationship}
          disabled={!newRelationship.relationshipType || !newRelationship.targetSphereId}
          variant="primary"
        />
      </div>
    </Dialog>
  );

  // Handle case where apiClients is not available
  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;

  return (
    <div className="relationship-grid-molecule">
      <Toast ref={toastRef} />

      {/* Collapsible header */}
      {collapsible ? (
        // A header holding action buttons cannot itself be a <button> (nested interactive controls):
        // the title is the toggle, and it fills the bar up to the actions.
        <div
          className="relationship-grid-header collapsible"
          style={{
            width: '100%',
            background: 'var(--component-background)',
            border: 'var(--primary-border)',
            borderRadius: isExpanded ? 'var(--border-radius) var(--border-radius) 0 0' : 'var(--border-radius)',
            color: 'var(--primary-text-color)',
            margin: 0,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: 'var(--font-family)',
            fontSize: 'var(--sub-heading-font)',
            fontWeight: '600',
            boxShadow: 'var(--card-shadow)',
          }}
        >
          <button
            type="button"
            className="relationship-grid-title"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              color: 'var(--primary-text-color)',
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              gap: '0.5rem',
              textAlign: 'start',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            <i className="pi pi-link" style={{ color: 'var(--primary-text-color)' }} />{' '}
            Sphere Relationships
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {headerActions}
            <i
              aria-hidden="true"
              className={`pi pi-chevron-right relationship-grid-toggle-icon ${isExpanded ? 'expanded' : ''}`}
              style={{ color: 'var(--secondary-text-color)', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            />
          </div>
        </div>
      ) : (
        <div 
          className="relationship-grid-header"
          style={{
            width: '100%',
            background: 'var(--component-background)',
            border: 'var(--primary-border)',
            borderRadius: 'var(--border-radius) var(--border-radius) 0 0',
            color: 'var(--primary-text-color)',
            margin: 0,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: 'var(--font-family)',
            fontSize: 'var(--sub-heading-font)',
            fontWeight: '600',
            boxShadow: 'var(--card-shadow)',
          }}
        >
          <div className="relationship-grid-title" style={{ 
            color: 'var(--primary-text-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="pi pi-link" style={{ color: 'var(--primary-text-color)' }} />{' '}
            Sphere Relationships
          </div>
          {headerActions}
        </div>
      )}

      {/* Grid content - only show when expanded or not collapsible */}
      {(isExpanded || !collapsible) && (
        <div className="relationship-grid-content">
          {isMobile ? (
            // Mobile Card View
            <div className="mobile-relationships-container">
              {relationshipsLoading && (
                <div className="mobile-loading">
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                  <span>Loading relationships...</span>
                </div>
              )}

              {relationshipsError && (
                <div className="mobile-error">
                  <i className="pi pi-exclamation-triangle" />
                  <span>{relationshipsError}</span>
                </div>
              )}

              {!relationshipsLoading && !relationshipsError && relationships.length === 0 && (
                <div className="mobile-empty">
                  <i className="pi pi-info-circle" />
                  <span>No relationships found</span>
                  {editable && (
                    <ButtonAtom
                      label="Add Relationship"
                      icon="pi pi-plus"
                      onClick={() => setIsAddingRelationship(true)}
                      size="small"
                      variant="primary"
                    />
                  )}
                </div>
              )}

              {!relationshipsLoading && !relationshipsError && relationships.map((relationship) => (
                <MobileRelationshipCard
                  key={relationship.id}
                  relationship={relationship}
                  relationshipTypeLabel={
                    availableRelationshipTypes.find((rt: RelationshipTypeOption) => rt.value === relationship.relationshipType)?.label ||
                    relationship.relationshipType
                  }
                  editable={editable}
                  sphereId={sphereId}
                  onRemove={handleRemoveRelationship}
                />
              ))}
            </div>
          ) : (
            // Desktop Table View
            <DataGridAtom
              data={relationships}
              columns={columns}
              loading={relationshipsLoading}
              error={relationshipsError}
              emptyMessage="No relationships found"
              actions={actions}
              showActions={editable}
              paginator={relationships.length > 10}
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              className="relationship-data-grid"
              stripedRows
              showHeader={false}
            />
          )}

          {/* Inline Add Relationship Row */}
          {isAddingRelationship && (
            <div ref={addRowRef} className="relationship-add-row">
              <div className="relationship-add-row-content">
                <div className="relationship-add-field relationship-type-field">
                  <Dropdown
                    value={newRelationshipData.relationshipType}
                    options={availableRelationshipTypes}
                    onChange={(e) => handleRelationshipTypeChange(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select relationship type"
                    className="relationship-type-dropdown"
                  />
                </div>
                
                {newRelationshipData.relationshipType && (
                  <div className="relationship-add-field sphere-search-field">
                    <SphereSearch
                      relationshipType={newRelationshipData.relationshipType}
                      organizationId={effectiveOrganizationId}
                      onSphereSelected={(selectedSpheres, selectedSphere) => {
                        setNewRelationshipData(prev => ({
                          ...prev,
                          targetSphere: selectedSphere?.sphere || null,
                        }));
                      }}
                      filters={getSearchFiltersForRelationshipType(newRelationshipData.relationshipType)}
                      filterOperator="and"
                      placeholder="Search for sphere to relate..."
                      single={true}
                      showLabel={false}
                      className="relationship-sphere-search"
                      contextFilters={contextFilters}
                    />
                  </div>
                )}


                <div className="relationship-add-field actions-field">
                  <div className="relationship-add-actions">
                    <ButtonAtom
                      label="Relate"
                      icon="pi pi-check"
                      onClick={handleInlineCreateRelationship}
                      size="small"
                      variant="primary"
                      disabled={!newRelationshipData.relationshipType || !newRelationshipData.targetSphere}
                      className="relate-button"
                      type="button"
                    />
                    <ButtonAtom
                      label="Cancel"
                      icon="pi pi-times"
                      onClick={handleCancelAddRelationship}
                      size="small"
                      variant="secondary"
                      outlined
                      className="cancel-button"
                      type="button"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {addRelationshipDialog}
    </div>
  );
};

export default RelationshipGridMolecule;